import type { Surcharge, SurchargeRate } from '@api/useFreight/types'
import { useRateManagement as useApiRateManagement } from '@api/useFreight/useFreight'
import { message } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import type dayjs from 'dayjs'

/**
 * Custom hook for managing surcharge rates
 * @param surcharge The parent surcharge object
 * @param onSuccess Optional callback after successful operations
 * @returns Object containing rate management functions
 */
export const useRateManagement = (surcharge: Surcharge, onSuccess?: () => void) => {
  const queryClient = useQueryClient()
  const { useEditSurchargeRate, useDeleteSurchargeRate, useCreateSurchargeRate } = useApiRateManagement()

  const editRateMutation = useEditSurchargeRate()
  const deleteRateMutation = useDeleteSurchargeRate()
  const createRateMutation = useCreateSurchargeRate()

  const isPercentageType = surcharge.type === 'Percentage'

  /**
   * Edit an existing surcharge rate
   * @param rate The rate to edit
   * @param values Updated values for the rate
   * @param hasEndDate Whether the rate has an end date
   */
  const editRate = async (
    rate: SurchargeRate,
    values: {
      rate: number
      effective_date: dayjs.Dayjs
      effective_to: dayjs.Dayjs | null
    },
    hasEndDate: boolean
  ) => {
    try {
      const effectiveDate = values.effective_date.toISOString()
      const effectiveTo = hasEndDate && values.effective_to ? values.effective_to.toISOString() : undefined
      const surchargeId = surcharge._id
      const apiRate = isPercentageType ? Number((values.rate / 100).toFixed(5)) : values.rate

      await editRateMutation.mutateAsync({
        surcharge_id: surchargeId,
        effective_date: effectiveDate,
        effective_to: effectiveTo,
        surcharge: apiRate,
        rate_id: rate.id,
      })

      message.success('Rate updated successfully')
      await queryClient.invalidateQueries({ queryKey: ['surcharges'] })

      if (onSuccess) {
        onSuccess()
      }

      return true
    } catch (error) {
      console.error('Failed to update rate:', error)
      message.error('Failed to update rate')
      return false
    }
  }

  /**
   * Delete a surcharge rate
   * @param rate The rate to delete
   */
  const deleteRate = async (rate: SurchargeRate) => {
    try {
      await deleteRateMutation.mutateAsync({
        surcharge_id: surcharge._id,
        effective_date: rate.effective_from,
        effective_to: rate.effective_to || undefined,
      })

      message.success('Rate deleted successfully')
      await queryClient.invalidateQueries({ queryKey: ['surcharges'] })

      if (onSuccess) {
        onSuccess()
      }

      return true
    } catch (error) {
      console.error('Failed to delete rate:', error)
      message.error('Failed to delete rate')
      return false
    }
  }

  /**
   * Create a new scheduled rate
   * @param values The new rate values
   * @param hasEndDate Whether the rate has an end date
   */
  const scheduleRate = async (
    values: {
      rate: number
      effective_date: dayjs.Dayjs
      effective_to?: dayjs.Dayjs
    },
    hasEndDate: boolean
  ) => {
    try {
      // Convert from percentage (50) to decimal (0.5) for API if this is a percentage surcharge
      let surchargeValue = values.rate
      if (surcharge.type === 'Percentage') {
        surchargeValue /= 100
      }

      await createRateMutation.mutateAsync({
        surcharge_id: surcharge._id,
        effective_date: values.effective_date.format('YYYY-MM-DD'),
        surcharge: surchargeValue,
        effective_to: hasEndDate && values.effective_to ? values.effective_to.format('YYYY-MM-DD') : undefined,
      })

      // Invalidate queries to refresh data
      await queryClient.invalidateQueries({ queryKey: ['surcharges'] })

      message.success('Rate scheduled successfully')
      if (onSuccess) {
        onSuccess()
      }

      return true
    } catch (error) {
      console.error('Failed to schedule rate:', error)
      message.error('Failed to schedule rate')
      return false
    }
  }

  return {
    isPercentageType,
    editRate,
    deleteRate,
    scheduleRate,
    editRateMutation,
    deleteRateMutation,
    createRateMutation,
  }
}
